@file:NoLiveLiterals

package com.example.borshi.web.home

import androidx.compose.runtime.*
import androidx.compose.runtime.NoLiveLiterals
import com.example.borshi.web.Screen
import com.example.borshi.web.cart.CartLineItem
import com.example.borshi.web.cart.CartStore
import com.example.borshi.web.firebase.FirebaseService
import com.example.borshi.web.model.*
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

// ─────────────────────────────────────────────────────────────────────────────
// Design Tokens
// ─────────────────────────────────────────────────────────────────────────────
private object DS {
    const val ink        = "#0D2B2E"
    const val inkMid     = "#3D5A5E"
    const val inkLight   = "#7A9598"
    const val teal       = "#26A69A"
    const val tealDeep   = "#1A7A72"
    const val tealGhost  = "#E0F2F1"
    const val tealFaint  = "#F0FAFA"
    const val crimson    = "#D11243"
    const val surface    = "#F5F9F8"
    const val white      = "#FFFFFF"
    const val border     = "#E0EDEB"
    const val borderDark = "#C4DBD8"
    const val success    = "#2E7D32"
    const val warn       = "#E65100"

    val sp4  = 4.px; val sp8  = 8.px; val sp10 = 10.px
    val sp12 = 12.px; val sp16 = 16.px; val sp20 = 20.px; val sp24 = 24.px
    val sp28 = 28.px; val sp32 = 32.px; val sp40 = 40.px; val sp48 = 48.px
    val sp64 = 64.px

    val r8 = 8.px; val r12 = 12.px; val r16 = 16.px
    val r20 = 20.px; val rFull = 999.px

    const val shadowSm   = "0 1px 4px rgba(13,43,46,0.06)"
    const val shadowMd   = "0 4px 16px rgba(13,43,46,0.10)"
    const val shadowLg   = "0 8px 32px rgba(13,43,46,0.14)"
    const val shadowXl   = "0 20px 60px rgba(13,43,46,0.18)"
    const val shadowTeal = "0 4px 16px rgba(38,166,154,0.28)"
    const val shadowTealLg = "0 12px 40px rgba(38,166,154,0.35)"
}

// ─────────────────────────────────────────────────────────────────────────────
// Category helpers — single source of truth
// ─────────────────────────────────────────────────────────────────────────────
internal fun planCategoryEmoji(name: String) = when (name) {
    "MUSCLE_BUILDING" -> "💪"; "WEIGHT_LOSS" -> "🔥"; "HEART_HEALTH" -> "❤️"
    "ATHLETE"         -> "🏃"; "DIABETES"    -> "💉"; "PREGNANCY"    -> "🤰"
    "SENIOR"          -> "👴"; "PCOS"        -> "🌸"; else           -> "🌿"
}

internal fun planCategoryStyle(name: String): Pair<String, String> = when (name) {
    "MUSCLE_BUILDING" -> "#FFF3E0" to DS.warn
    "WEIGHT_LOSS"     -> "#FCE4EC" to DS.crimson
    "HEART_HEALTH"    -> "#FFEBEE" to "#B71C1C"
    "ATHLETE"         -> DS.tealGhost to DS.tealDeep
    "DIABETES"        -> "#FFF8E1" to "#F57F17"
    "PREGNANCY"       -> "#FCE4EC" to "#880E4F"
    "SENIOR"          -> "#E8EAF6" to "#283593"
    "PCOS"            -> "#F3E5F5" to "#6A1B9A"
    else              -> "#E8F5E9" to DS.success
}

// ─────────────────────────────────────────────────────────────────────────────
// CSS — injected once, includes every premium nutrition component
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun InjectHomeStyles() {
    DisposableEffect(Unit) {
        if (kotlinx.browser.document.getElementById("borshi-home-styles") != null) {
            return@DisposableEffect onDispose { }
        }
        val style = kotlinx.browser.document.createElement("style")
        style.id = "borshi-home-styles"
        style.textContent = """
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: 'DM Sans', sans-serif; background: ${DS.surface}; color: ${DS.ink}; -webkit-font-smoothing: antialiased; }

::-webkit-scrollbar { width: 5px; height: 5px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: ${DS.borderDark}; border-radius: 99px; }
::-webkit-scrollbar-thumb:hover { background: ${DS.teal}; }

/* ── Keyframes ── */
@keyframes fadeUp    { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
@keyframes shimmer   { 0%{background-position:-700px 0} 100%{background-position:700px 0} }
@keyframes pulse     { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.7;transform:scale(.95)} }
@keyframes subtlePulse { 0%,100%{box-shadow:${DS.shadowTeal}} 50%{box-shadow:0 4px 28px rgba(38,166,154,0.52)} }
@keyframes slideIn   { from{opacity:0;transform:translateX(-12px)} to{opacity:1;transform:translateX(0)} }

.fade-up  { animation:fadeUp  0.55s cubic-bezier(0.22,1,0.36,1) both; }
.slide-in { animation:slideIn 0.45s cubic-bezier(0.22,1,0.36,1) both; }
.delay-1  { animation-delay:.07s; } .delay-2 { animation-delay:.14s; }
.delay-3  { animation-delay:.21s; } .delay-4 { animation-delay:.28s; }

.skeleton { background:linear-gradient(90deg,#E4EFEE 25%,#F2F9F8 50%,#E4EFEE 75%); background-size:700px 100%; animation:shimmer 1.5s ease-in-out infinite; border-radius:14px; }

/* ── Section typography ── */
.sec-eyebrow { font-family:'Syne',sans-serif; font-size:10px; font-weight:700; letter-spacing:.18em; text-transform:uppercase; color:${DS.teal}; margin-bottom:6px; }
.sec-heading  { font-family:'Syne',sans-serif; font-size:32px; font-weight:800; color:${DS.ink}; letter-spacing:-.045em; line-height:1.08; }
.sec-subtext  { font-size:13.5px; color:${DS.inkLight}; line-height:1.7; font-weight:400; }

/* ── Divider ── */
.sec-divider { height:1px; background:linear-gradient(90deg,transparent,${DS.borderDark},transparent); margin:64px 0; }

/* ── Mode toggle ── */
.mode-toggle { display:inline-flex; gap:4px; background:${DS.white}; border:1.5px solid ${DS.border}; border-radius:999px; padding:4px; box-shadow:${DS.shadowSm}; }
.mode-btn    { padding:10px 26px; border-radius:999px; font-size:14px; font-weight:600; cursor:pointer; border:none; background:transparent; color:${DS.inkMid}; font-family:'DM Sans',sans-serif; transition:all .2s ease; white-space:nowrap; }
.mode-btn.active { background:${DS.ink}; color:white; box-shadow:${DS.shadowSm}; }

/* ── Hero ── */
.hero-card { border-radius:22px; overflow:hidden; cursor:pointer; border:2px solid transparent; position:relative; transition:transform .28s cubic-bezier(0.22,1,0.36,1),box-shadow .28s ease,border-color .28s ease; }
.hero-card:hover  { transform:translateY(-6px); box-shadow:${DS.shadowLg}; }
.hero-card.active { border-color:${DS.teal}; box-shadow:${DS.shadowTeal}; }

/* ── Category card ── */
.cat-card { background:${DS.white}; border-radius:18px; border:1.5px solid ${DS.border}; overflow:hidden; cursor:pointer; text-align:center; transition:transform .22s cubic-bezier(0.22,1,0.36,1),box-shadow .22s ease,border-color .22s ease; display:flex; flex-direction:column; align-items:center; }
.cat-card:hover { transform:translateY(-5px); box-shadow:${DS.shadowMd}; border-color:${DS.teal}; }

/* ── Product card ── */
.product-card { background:${DS.white}; border-radius:20px; border:1.5px solid ${DS.border}; overflow:hidden; transition:transform .22s cubic-bezier(0.22,1,0.36,1),box-shadow .22s ease; display:flex; flex-direction:column; }
.product-card:hover { transform:translateY(-5px); box-shadow:${DS.shadowLg}; }
.purchase-mode-tab { flex:1; padding:8px; border-radius:8px; font-size:12px; font-weight:600; text-align:center; cursor:pointer; border:none; font-family:'DM Sans',sans-serif; transition:all .18s ease; }

/* ── Urgency ── */
.urgency-banner { background:linear-gradient(135deg,${DS.ink} 0%,#1A4A4E 100%); border-radius:16px; padding:16px 28px; display:flex; justify-content:center; align-items:center; gap:20px; flex-wrap:wrap; box-shadow:${DS.shadowMd}; }

/* == HOW BORSHI WORKS — clean editorial redesign == */

.hiw-wrap {
    background: ${DS.white};
    border-radius: 28px;
    border: 1.5px solid ${DS.border};
    padding: 64px 60px;
    position: relative;
    overflow: hidden;
}

/* Subtle background texture: two very faint corner marks */
.hiw-wrap::before {
    content: '';
    position: absolute; top: 0; right: 0;
    width: 320px; height: 320px;
    background: radial-gradient(circle at top right, rgba(38,166,154,.045) 0%, transparent 65%);
    pointer-events: none;
}

/* Cards grid */
.hiw-cards { display:grid; grid-template-columns:1fr 1fr; gap:20px; margin-top:48px; }

/* Individual card — clean, minimal */
.hiw-card {
    border-radius:20px; border:1.5px solid ${DS.border};
    background:${DS.surface};
    padding:32px 32px 28px;
    display:flex; flex-direction:column;
    position:relative; overflow:hidden; cursor:pointer;
    transition:transform .28s cubic-bezier(0.22,1,0.36,1), box-shadow .28s ease, border-color .28s ease;
}
/* 4-px accent bar — the ONLY color on each card */
.hiw-card::before {
    content:''; position:absolute;
    left:0; top:0; bottom:0; width:4px; border-radius:2px 0 0 2px;
}
.hiw-card.diy::before     { background:${DS.teal}; }
.hiw-card.guided::before  { background:#D4A017; }
.hiw-card:hover { transform:translateY(-5px); border-color:transparent; }
.hiw-card.diy:hover    { box-shadow:0 16px 48px rgba(38,166,154,.13); }
.hiw-card.guided:hover { box-shadow:0 16px 48px rgba(212,160,23,.13); }

/* Path label (eyebrow) */
.hiw-path-label {
    font-family:'DM Sans',sans-serif; font-size:10px; font-weight:800;
    letter-spacing:.14em; text-transform:uppercase; margin-bottom:14px;
}
.hiw-path-label.diy-label     { color:${DS.teal}; }
.hiw-path-label.guided-label  { color:#B8860B; }

/* Icon */
.hiw-card-icon { font-size:36px; line-height:1; margin-bottom:12px; }

/* Title + sub */
.hiw-card-title {
    font-family:'Syne',sans-serif; font-size:21px; font-weight:800;
    color:${DS.ink}; letter-spacing:-.03em; line-height:1.2; margin-bottom:8px;
}
.hiw-card-sub {
    font-family:'DM Sans',sans-serif; font-size:13px; color:${DS.inkLight};
    line-height:1.65; margin-bottom:22px;
}

/* Steps */
.hiw-steps { display:flex; flex-direction:column; flex:1; margin-bottom:24px; }
.hiw-step {
    display:flex; align-items:flex-start; gap:12px; padding:9px 0;
    border-bottom:1px solid ${DS.border};
}
.hiw-step:last-child { border-bottom:none; }
.hiw-step-num {
    width:21px; height:21px; border-radius:50%; flex-shrink:0;
    display:flex; align-items:center; justify-content:center;
    font-family:'Syne',sans-serif; font-size:10px; font-weight:800;
    margin-top:1px; background:${DS.tealGhost}; color:${DS.tealDeep};
}
.hiw-step-num.guided-num { background:#FFF3CD; color:#8A6500; }
.hiw-step-text { font-family:'DM Sans',sans-serif; font-size:13px; font-weight:500; color:${DS.inkMid}; line-height:1.5; }

/* CTAs */
.hiw-card-cta {
    display:flex; align-items:center; justify-content:center; gap:6px;
    width:100%; padding:13px 20px; border-radius:12px;
    font-size:13px; font-weight:700; cursor:pointer; font-family:'DM Sans',sans-serif;
    transition:all .2s cubic-bezier(0.22,1,0.36,1); letter-spacing:.01em; margin-top:auto;
}
.hiw-card-cta.diy-cta    { background:${DS.ink}; color:white; border:none; }
.hiw-card-cta.diy-cta:hover    { background:${DS.teal}; transform:translateY(-2px); box-shadow:${DS.shadowTeal}; }
.hiw-card-cta.guided-cta { background:${DS.white}; color:${DS.ink}; border:1.5px solid ${DS.borderDark}; }
.hiw-card-cta.guided-cta:hover { background:${DS.ink}; color:white; border-color:${DS.ink}; transform:translateY(-2px); }

/* Bottom trust bar */
.hiw-trust {
    display:flex; align-items:center; justify-content:center;
    gap:32px; margin-top:40px; flex-wrap:wrap;
}
.hiw-trust-item {
    font-family:'DM Sans',sans-serif; font-size:11.5px; font-weight:600;
    color:${DS.inkLight}; display:flex; align-items:center; gap:6px;
}

/* Responsive */
@media(max-width:700px) {
    .hiw-wrap { padding:40px 24px; }
    .hiw-cards { grid-template-columns:1fr; }
    .hiw-card-title { font-size:19px; }
}

/* == EXPERT PORTRAIT TILES — premium magazine-cover with doctor photo == */
.expert-tile {
    background:${DS.white}; border-radius:24px; border:1.5px solid ${DS.border};
    overflow:hidden; display:flex; flex-direction:column;
    transition:transform .3s cubic-bezier(0.22,1,0.36,1), box-shadow .3s ease, border-color .3s ease;
    cursor:pointer;
}
.expert-tile:hover { transform:translateY(-10px); box-shadow:${DS.shadowXl}; border-color:${DS.teal}; }

/* Portrait — tall 3:4, full magazine cover */
.expert-photo {
    width:100%; aspect-ratio:3/4; position:relative; overflow:hidden; flex-shrink:0;
    background:linear-gradient(145deg,${DS.tealGhost} 0%,${DS.surface} 100%);
    display:flex; align-items:center; justify-content:center;
}
.expert-photo img { width:100%; height:100%; object-fit:cover; display:block; transition:transform .45s ease; }
.expert-tile:hover .expert-photo img { transform:scale(1.05); }

/* Initials fallback */
.expert-initials { width:100%; height:100%; display:flex; flex-direction:column;
    align-items:center; justify-content:center; gap:8px;
    background:linear-gradient(145deg,${DS.ink} 0%,#1A7A72 100%); }
.expert-initials-letter { font-family:'Syne',sans-serif; font-size:68px; font-weight:800;
    color:rgba(255,255,255,.88); line-height:1; text-shadow:0 4px 24px rgba(0,0,0,.32); }
.expert-initials-hint { font-size:10px; font-weight:700; letter-spacing:.12em; text-transform:uppercase;
    color:rgba(255,255,255,.35); margin-top:4px; }

/* Verified badge — top-right of photo */
.expert-verified { position:absolute; top:12px; right:12px;
    background:${DS.teal}; color:white; border-radius:999px;
    font-size:10px; font-weight:800; padding:4px 10px; letter-spacing:.04em;
    box-shadow:0 2px 8px rgba(38,166,154,.45); }

/* Dark gradient + info overlaid at photo bottom */
.expert-photo-overlay { position:absolute; bottom:0; left:0; right:0; height:68%;
    background:linear-gradient(to top,rgba(9,30,34,.94) 0%,rgba(9,30,34,.42) 55%,transparent 100%); }
.expert-photo-info { position:absolute; bottom:0; left:0; right:0; padding:18px 18px 20px; }
.expert-name  { font-family:'Syne',sans-serif; font-size:16px; font-weight:800; color:white;
    line-height:1.2; margin-bottom:3px; text-shadow:0 1px 8px rgba(0,0,0,.5); }
.expert-quals { font-size:11px; font-weight:600; color:${DS.teal};
    text-shadow:0 1px 4px rgba(0,0,0,.4); margin-bottom:8px; }
.expert-rating-pill { display:inline-flex; align-items:center; gap:4px;
    background:rgba(13,43,46,.62); backdrop-filter:blur(8px);
    border-radius:999px; padding:3px 10px; font-size:12px; font-weight:800; color:#FFD54F; }

/* Card body */
.expert-body { padding:18px 18px 22px; display:flex; flex-direction:column; gap:13px; flex:1; }
.expert-spec { display:inline-block; font-size:10px; font-weight:800; letter-spacing:.06em;
    text-transform:uppercase; color:${DS.tealDeep}; background:${DS.tealGhost};
    padding:3px 11px; border-radius:999px; }
.expert-bio { font-size:13px; color:${DS.inkMid}; line-height:1.65;
    display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }

.expert-stats { display:grid; grid-template-columns:repeat(3,1fr); gap:1px;
    background:${DS.border}; border-radius:12px; overflow:hidden; }
.expert-stat { background:${DS.white}; padding:10px 6px; text-align:center; }
.expert-stat-val { font-family:'Syne',sans-serif; font-size:15px; font-weight:800; color:${DS.ink}; }
.expert-stat-label { font-size:10px; font-weight:600; color:${DS.inkLight}; text-transform:uppercase; letter-spacing:.06em; }

.expert-plan-pills { display:flex; gap:6px; flex-wrap:wrap; }
.expert-plan-pill { font-size:10px; font-weight:700; padding:3px 9px; border-radius:999px; white-space:nowrap; }

.expert-cta { display:flex; align-items:center; justify-content:center; gap:8px;
    width:100%; padding:13px; border-radius:14px; border:none; font-size:13px;
    font-weight:700; cursor:pointer; font-family:'DM Sans',sans-serif;
    background:${DS.ink}; color:white; transition:all .18s ease; letter-spacing:.01em; }
.expert-cta:hover { background:#1A3D40; transform:translateY(-1px); box-shadow:${DS.shadowMd}; }

/* == PLAN CARDS — pixel-identical to Plans tab (ProteinPlansScreen) == */
.plan-card {
    background:${DS.white}; border-radius:20px; border:1.5px solid ${DS.border};
    overflow:hidden; display:flex; flex-direction:column; cursor:pointer;
    transition:transform .24s cubic-bezier(0.22,1,0.36,1), box-shadow .24s ease, border-color .24s ease;
}
.plan-card:hover { transform:translateY(-6px); box-shadow:${DS.shadowLg}; border-color:${DS.teal}; }

.plan-thumb {
    width:100%; height:165px; position:relative; overflow:hidden; flex-shrink:0;
    background:linear-gradient(135deg,${DS.tealGhost},${DS.surface});
    display:flex; align-items:center; justify-content:center;
}
.plan-thumb img { width:100%; height:100%; object-fit:cover; transition:transform .4s ease; }
.plan-card:hover .plan-thumb img { transform:scale(1.05); }
.plan-cat-pill { position:absolute; top:10px; left:10px; font-size:10px; font-weight:800;
    padding:4px 11px; border-radius:999px; box-shadow:0 2px 8px rgba(0,0,0,.14); }
.plan-dr-credit { position:absolute; bottom:10px; right:10px; font-size:10px; font-weight:700;
    color:white; background:rgba(13,43,46,.72); backdrop-filter:blur(6px);
    padding:3px 10px; border-radius:999px; }

.plan-body { padding:18px; display:flex; flex-direction:column; gap:10px; flex:1; }
.plan-name { font-family:'Syne',sans-serif; font-size:15px; font-weight:800; color:${DS.ink}; line-height:1.3; }
.plan-desc { font-size:12px; color:${DS.inkLight}; line-height:1.55;
    display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.plan-chips { display:flex; gap:5px; flex-wrap:wrap; }
.plan-chip  { font-size:10px; font-weight:700; padding:3px 8px; border-radius:999px; white-space:nowrap; }
.plan-spacer { flex:1; }
.plan-footer { display:flex; justify-content:space-between; align-items:center;
    border-top:1px solid ${DS.border}; padding-top:12px; margin-top:4px; }
.plan-price { font-family:'Syne',sans-serif; font-size:20px; font-weight:800; color:${DS.crimson}; }
.plan-price-unit { font-size:11px; color:${DS.inkLight}; font-weight:400; }
.plan-view-btn { padding:8px 20px; border-radius:999px; border:none; background:${DS.ink}; color:white;
    font-size:12px; font-weight:700; cursor:pointer; font-family:'DM Sans',sans-serif;
    transition:all .15s ease; }
.plan-view-btn:hover { background:#1A3D40; box-shadow:${DS.shadowMd}; }

/* ── Buttons ── */
.btn-ink  { display:inline-flex; align-items:center; gap:8px; padding:13px 32px; border-radius:999px; font-size:14px; font-weight:700; cursor:pointer; border:none; background:${DS.ink}; color:white; font-family:'DM Sans',sans-serif; transition:all .2s ease; box-shadow:${DS.shadowMd}; }
.btn-ink:hover  { background:#1A3D40; transform:translateY(-2px); box-shadow:${DS.shadowLg}; }
.btn-outline { display:inline-flex; align-items:center; gap:8px; padding:11px 24px; border-radius:999px; font-size:13px; font-weight:700; cursor:pointer; border:1.5px solid ${DS.border}; background:${DS.white}; color:${DS.ink}; font-family:'DM Sans',sans-serif; transition:all .18s ease; }
.btn-outline:hover { border-color:${DS.teal}; color:${DS.teal}; }

/* ── What motivates us — compact feedback strip, horizontal scroll ── */
.wmu-scroll {
    overflow-x: auto;
    overflow-y: hidden;
    width:100%;
    margin-top:16px;
    padding-bottom:8px;
    -webkit-overflow-scrolling: touch;
}
.wmu-track {
    display:flex;
    gap:12px;
    width:max-content;
}
.wmu-card {
    flex-shrink:0;
    width:320px;
    height:200px;
    border-radius:14px;
    overflow:hidden;
    border:1.5px solid ${DS.border};
    background:${DS.white};
    box-shadow:${DS.shadowSm};
    transition:transform .22s ease, box-shadow .22s ease, border-color .22s ease;
}
.wmu-card:hover { transform:translateY(-3px); box-shadow:${DS.shadowMd}; border-color:${DS.teal}; }
.wmu-card img { width:100%; height:100%; object-fit:cover; display:block; }
.wmu-shimmer {
    flex-shrink:0;
    width:320px;
    height:200px;
    border-radius:14px;
    background:linear-gradient(90deg,#E4EFEE 25%,#F2F9F8 50%,#E4EFEE 75%);
    background-size:400px 100%;
    animation:shimmer 1.5s ease-in-out infinite;
}

/* ── Responsive ── */
@media (max-width:860px) {
    .hiw-cards  { grid-template-columns:1fr !important; }
    .expert-grid { grid-template-columns:repeat(2,1fr) !important; }
    .plan-grid  { grid-template-columns:repeat(2,1fr) !important; }
    .sec-heading { font-size:24px; }
}
@media (max-width:600px) {
    .hero-grid  { grid-template-columns:1fr !important; }
    .mode-toggle { width:100%; }
    .mode-btn { flex:1; padding:10px 12px; font-size:13px; }
    .expert-grid { grid-template-columns:1fr !important; }
    .plan-grid  { grid-template-columns:1fr !important; }
    .sec-heading { font-size:20px; }
}
        """.trimIndent()
        kotlinx.browser.document.head?.appendChild(style)
        onDispose { }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Main HomeScreen — wired to ViewModel; zero raw state, zero magic strings
// ─────────────────────────────────────────────────────────────────────────────
@Composable
fun HomeScreen(
    onNavigate: (Screen) -> Unit,
    cartStore: CartStore? = null,
    onAddToCart: (Product) -> Unit
) {
    InjectHomeStyles()

    // ── ViewModel ─────────────────────────────────────────────────────────
    val scope    = remember { MainScope() }
    val vm       = rememberHomeViewModel(scope)
    val state    by vm.state

    // ── Bootstrap & timer ─────────────────────────────────────────────────
    LaunchedEffect(Unit) {
        vm.dispatch(HomeScreenAction.LoadAdhocData)
        // Tick every second — no memory leak; effect is cancelled on dispose
        window.setInterval({
            vm.dispatch(HomeScreenAction.TickTimer)
        }, AppConfig.TIMER_INTERVAL_MS)
    }

    Div({
        style { backgroundColor(Color(DS.surface)); minHeight(100.vh) }
        attr("id", "home-root")
    }) {

        // ── Brand announcement strip ───────────────────────────────────────
        BrandAnnouncementStrip()

        Div({
            style {
                maxWidth(AppConfig.BREAKPOINT_DESKTOP.px)
                property("margin", "0 auto")
                padding(DS.sp24, DS.sp16, DS.sp64)
                width(100.percent)
            }
            attr("id", "main-content")
            ariaRole(AriaRole.Main)
        }) {

            // ── Dismissible error banner ───────────────────────────────
            val err = state.error
            if (err != null) {
                Div({
                    style {
                        backgroundColor(Color("#FFF8F0"))
                        border(1.px, LineStyle.Solid, Color("#FFD0A0"))
                        borderRadius(DS.r12)
                        padding(DS.sp12, DS.sp16)
                        marginBottom(DS.sp16)
                        fontSize(13.px)
                        color(Color("#A34A00"))
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                    }
                    ariaLive(AriaPoliteness.Polite)
                    ariaRole(AriaRole.Alert)
                }) {
                    Text("⚠️ ${err.userMessage}")
                    Button({
                        style {
                            background("none")
                            border(0.px)
                            cursor("pointer")
                            color(Color("#A34A00"))
                            fontSize(16.px)
                            padding(0.px)
                        }
                        ariaLabel("Dismiss error")
                        onClick { vm.dispatch(HomeScreenAction.ClearError) }
                    }) { Text("✕") }
                }
            }

            // ── Hero + mode toggle ─────────────────────────────────────
            HeroDualEntry(
                viewMode   = state.viewMode,
                onBrowse   = { vm.dispatch(HomeScreenAction.ChangeViewMode(ViewMode.ADHOC)) },
                onPlans    = { vm.dispatch(HomeScreenAction.ChangeViewMode(ViewMode.SUBSCRIPTION)) }
            )

            Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); margin(DS.sp32, 0.px) } }) {
                Div({
                    classes("mode-toggle")
                    ariaRole(AriaRole.TabList)
                    ariaLabel("Browse mode")
                }) {
                    listOf(ViewMode.ADHOC to "Daily Pre-orders", ViewMode.SUBSCRIPTION to "Protein Plans").forEach { (mode, label) ->
                        Button({
                            val isActive = state.viewMode == mode
                            attr("class", if (isActive) "mode-btn active" else "mode-btn")
                            ariaRole(AriaRole.Tab)
                            ariaExpanded(isActive)
                            ariaLabel(label)
                            onClick { vm.dispatch(HomeScreenAction.ChangeViewMode(mode)) }
                        }) { Text(label) }
                    }
                }
            }

            // ── Content pane ──────────────────────────────────────────
            when (state.viewMode) {
                ViewMode.ADHOC        -> AdhocView(
                    onNavigate       = onNavigate,
                    categories       = state.categories,
                    featuredProducts = state.featuredProducts,
                    cartStore        = cartStore,
                    onAddToCart      = onAddToCart,
                    urgencyTimer     = state.urgencyTimer,
                    isLoading        = state.showAdhocSkeleton
                )
                ViewMode.SUBSCRIPTION -> SubscriptionView(
                    onNavigate    = onNavigate,
                    nutritionists = state.nutritionists,
                    plans         = state.plans,
                    isLoading     = state.showSubSkeleton
                )
            }

            // ── What Motivates Us — always shown below both view modes ─
            Div({ classes("sec-divider") }) {}
            WhatMotivatesUsSection(feedbackImages = state.feedbackImages)
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// What Motivates Us — auto-sliding horizontal strip of customer feedback images
// Loaded from Firebase Storage feedback/ folder. Theme-aligned compact cards.
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun WhatMotivatesUsSection(feedbackImages: List<String>) {
    Div({ style { marginTop(DS.sp32) } }) {
        // Section heading — same scale as other section headings
        Div({ style { marginBottom(DS.sp16) } }) {
            H2({ classes("sec-heading") }) { Text("What motivates us?") }
            P({ classes("sec-subtext"); style { marginTop(8.px) } }) { Text("Real stories from our community") }
        }

        // Horizontal scroll strip (user scrolls)
        Div({ classes("wmu-scroll") }) {
            if (feedbackImages.isEmpty()) {
                Div({ classes("wmu-track") }) {
                    repeat(4) { Div({ classes("wmu-shimmer") }) {} }
                }
            } else {
                Div({ classes("wmu-track") }) {
                    feedbackImages.forEach { url ->
                        Div({ classes("wmu-card") }) {
                            Img(
                                src = url,
                                alt = "Customer feedback",
                                attrs = { style { width(100.percent); height(100.percent); property("object-fit", "cover") } }
                            )
                        }
                    }
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Brand announcement strip — full-width, below header, above content
// ─────────────────────────────────────────────────────────────────────────────
@Composable
fun BrandAnnouncementStrip() {
    Div({
        style {
            backgroundColor(Color(DS.ink))
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            gap(24.px)
            padding(8.px, 16.px)
            property("flex-wrap", "wrap")
        }
    }) {
        listOf(
            "🐟" to "Farm-fresh seafood daily",
            "⏰" to "Order by 8 PM · Next-day delivery",
            "🚚" to "Free delivery above ₹500"
        ).forEach { (icon, text) ->
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(6.px)
                }
            }) {
                Span({ style { fontSize(13.px) }}) { Text(icon) }
                Span({
                    style {
                        fontSize(11.5.px)
                        fontWeight("600")
                        color(Color("rgba(255,255,255,0.78)"))
                        property("letter-spacing", "0.02em")
                        fontFamily("DM Sans, sans-serif")
                        property("white-space", "nowrap")
                    }
                }) { Text(text) }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Hero — two sliding banner containers, left=window1, right=window2
// Images from Firebase Storage: sliding_banner/webapp/window1 and window2
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun HeroDualEntry(viewMode: ViewMode, onBrowse: () -> Unit, onPlans: () -> Unit) {
    var window1Urls by remember { mutableStateOf<List<String>>(emptyList()) }
    var window2Urls by remember { mutableStateOf<List<String>>(emptyList()) }

    LaunchedEffect(Unit) {
        coroutineScope {
            launch { window1Urls = FirebaseService.getSlidingBannerUrls("sliding_banner/webapp/window1") }
            launch { window2Urls = FirebaseService.getSlidingBannerUrls("sliding_banner/webapp/window2") }
        }
    }

    Div({ style { display(DisplayStyle.Grid); property("grid-template-columns","repeat(2,1fr)"); gap(DS.sp16) }; classes("hero-grid") }) {
        HeroSlidingCard(
            imageUrls = window1Urls,
            fallbackUrl = "https://firebasestorage.googleapis.com/v0/b/borshi-6fafe.firebasestorage.app/o/sliding_banner%2Fitem_banner1.webp?alt=media",
            label = "Daily Fresh",
            sub = "Pre-order by 8 PM",
            btnText = "Browse Items",
            isActive = viewMode == ViewMode.ADHOC,
            onClick = onBrowse
        )
        HeroSlidingCard(
            imageUrls = window2Urls,
            fallbackUrl = "https://firebasestorage.googleapis.com/v0/b/borshi-6fafe.firebasestorage.app/o/sliding_banner%2Fnutri_banner2.webp?alt=media",
            label = "Protein Plans",
            sub = "Expert-curated nutrition",
            btnText = "View Plans",
            isActive = viewMode == ViewMode.SUBSCRIPTION,
            onClick = onPlans
        )
    }
}

@Composable
private fun HeroSlidingCard(
    imageUrls: List<String>,
    fallbackUrl: String,
    label: String,
    sub: String,
    btnText: String,
    isActive: Boolean,
    onClick: () -> Unit
) {
    val urls = if (imageUrls.isEmpty()) listOf(fallbackUrl) else imageUrls
    var currentIndex by remember { mutableStateOf(0) }
    val currentUrl = urls[currentIndex % urls.size]

    LaunchedEffect(urls.size) {
        if (urls.size <= 1) return@LaunchedEffect
        while (true) {
            delay(3500L)
            currentIndex = (currentIndex + 1) % urls.size
        }
    }

    Div({
        attr("class", if (isActive) "hero-card active" else "hero-card")
        style { height(320.px); display(DisplayStyle.Flex); alignItems(AlignItems.FlexEnd); justifyContent(JustifyContent.Center); paddingBottom(DS.sp32); property("box-shadow",if (isActive) DS.shadowTeal else DS.shadowSm) }
        makeAccessible(label = "$label - $sub", role = AriaRole.Button)
        onKeyActivate { onClick() }
        onClick { onClick() }
    }) {
        Div({ style { position(Position.Absolute); top(0.px); left(0.px); right(0.px); bottom(0.px); property("background-image","url('$currentUrl')"); property("background-size","cover"); property("background-position","center"); property("transition","background-image 0.7s ease-in-out") }}) {}
        Div({ style { position(Position.Absolute); bottom(0.px); left(0.px); right(0.px); height(160.px); property("background","linear-gradient(to top,rgba(13,43,46,.80) 0%,transparent 100%)") }}) {}
        Div({ style { position(Position.Relative); textAlign("center"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center); gap(DS.sp4) }}) {
            Div({ style { fontFamily("Syne, sans-serif"); fontSize(22.px); fontWeight("800"); color(Color(DS.white)); property("text-shadow","0 2px 12px rgba(0,0,0,.55)"); property("letter-spacing","-0.02em") }}) { Text(label) }
            Div({ style { fontSize(12.5.px); color(Color("rgba(255,255,255,.80)")); fontWeight("500"); marginBottom(DS.sp16); property("letter-spacing","0.01em") }}) { Text(sub) }
            Div({ style { backgroundColor(Color(if (isActive) DS.teal else DS.white)); color(Color(if (isActive) DS.white else DS.ink)); padding(DS.sp8, DS.sp28); borderRadius(DS.rFull); fontSize(13.px); fontWeight("700"); property("box-shadow",DS.shadowMd); property("transition","all .2s ease") }}) { Text(btnText) }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Ad-hoc view
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun AdhocView(
    onNavigate:       (Screen) -> Unit,
    categories:       List<Category>,
    featuredProducts: List<Product>,
    cartStore:        CartStore?,
    onAddToCart:      (Product) -> Unit,
    urgencyTimer:     UrgencyTimerState,
    isLoading:        Boolean
) {
    PreOrderUrgencyBanner(urgencyTimer)
    Div({ classes("sec-divider") }) {}
    SectionHeader("Shop", "Browse by Category", null)
    if (isLoading) {
        CategoriesSkeleton()
    } else {
        CategoriesGrid(onNavigate, categories)
    }
    Div({ classes("sec-divider") }) {}
    SectionHeader("Today's Picks", "Featured Products", null)
    FeaturedProductsGrid(onAddToCart, featuredProducts, cartStore)
    Div({ style { textAlign("center"); marginTop(DS.sp32) }}) {
        Button({ classes("btn-ink"); onClick { onNavigate(Screen.Products()) } }) { Text("View All Products →") }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// ★ SUBSCRIPTION VIEW — final order:
//   ① How Borshi Plans Work   (dark card, DIY vs Dietitian paths)
//   ② Trusted by Nutrition Experts  (portrait tiles with doctor photo)
//   ③ Protein Plans  (full-size cards, identical to Plans tab)
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun SubscriptionView(
    onNavigate:    (Screen) -> Unit,
    nutritionists: List<NutritionistProfile>,
    plans:         List<NutritionistBasePlan>,
    isLoading:     Boolean
) {
    // ① How It Works — FIRST, anchors the whole perspective
    HowBorshiPlansWork(onNavigate)

    Div({ classes("sec-divider") }) {}

    // ② Expert portrait tiles
    ExpertsSection(nutritionists, plans, isLoading, onNavigate)

    Div({ classes("sec-divider") }) {}

    // ③ Plans — full-size, mirroring Plans tab
    PlansSection(plans, isLoading, onNavigate)
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared section header
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun SectionHeader(eyebrow: String, heading: String, actionLabel: String?, onAction: (() -> Unit)? = null) {
    Div({ style { marginBottom(DS.sp24) }}) {
        if (actionLabel != null && onAction != null) {
            Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.FlexEnd); flexWrap(FlexWrap.Wrap); gap(DS.sp16) }}) {
                Div {
                    Div({ classes("sec-eyebrow"); style { marginBottom(DS.sp8) }}) { Text(eyebrow) }
                    Div({ classes("sec-heading") }) { Text(heading) }
                }
                Button({ classes("btn-outline"); onClick { onAction() } }) { Text("$actionLabel →") }
            }
        } else {
            Div({ classes("sec-eyebrow"); style { marginBottom(DS.sp8) }}) { Text(eyebrow) }
            Div({ classes("sec-heading") }) { Text(heading) }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// ① HOW BORSHI PLANS WORK — Clean editorial redesign
//    White card, two minimal path cards with 4px accent borders only.
//    Path A: DIY Build-Your-Own  |  Path B: Guided by Dietitian
// ─────────────────────────────────────────────────────────────────────────────
@Composable
fun HowBorshiPlansWork(onNavigate: (Screen) -> Unit) {
    Div({ classes("hiw-wrap") }) {

        // ── Section header ────────────────────────────────────────────────
        Div({ style {
            textAlign("center")
            maxWidth(540.px); property("margin","0 auto")
            marginBottom(DS.sp48)
        }}) {
            Div({ classes("sec-eyebrow") }) { Text("How It Works") }
            Div({ classes("sec-heading"); style { marginTop(DS.sp8); marginBottom(DS.sp12) }}) {
                Text("Two paths to eating well")
            }
            Div({ classes("sec-subtext") }) {
                Text("Build your own custom seafood mix in minutes, or let a certified dietitian design the perfect plan for your body and goals.")
            }
        }

        // ── Two path cards ────────────────────────────────────────────────
        Div({ classes("hiw-cards") }) {

            // PATH A: DIY
            HiwPathCard(
                pathLabel   = "Path A · Self-Directed",
                labelClass  = "diy-label",
                cardClass   = "diy",
                icon        = "🧪",
                title       = "Build Your Own Mix",
                sub         = "Total control — pick every fish, set your macros, choose your delivery rhythm. No consultation needed.",
                steps       = listOf(
                    "Set your protein, calorie & omega-3 goals",
                    "Hand-pick fish & seafood from our live catalog",
                    "Choose your delivery rhythm — daily, alternate or custom"
                ),
                stepNumClass = "",
                ctaText     = "Start Building Your Mix",
                ctaClass    = "diy-cta",
                onClick     = { onNavigate(Screen.ProteinPlans) }
            )

            // PATH B: Expert Guided
            HiwPathCard(
                pathLabel   = "Path B · Expert Guided",
                labelClass  = "guided-label",
                cardClass   = "guided",
                icon        = "👩‍⚕️",
                title       = "Guided by Your Dietitian",
                sub         = "Expert-led precision — your certified nutritionist curates every choice. You just eat well and track progress.",
                steps       = listOf(
                    "Share your goals, health conditions & food preferences",
                    "Get matched with a certified nutritionist",
                    "Receive your personalised plan with weekly check-ins"
                ),
                stepNumClass = "guided-num",
                ctaText     = "Meet Your Dietitian",
                ctaClass    = "guided-cta",
                onClick     = { onNavigate(Screen.ProteinPlans) }
            )
        }

        // ── Trust bar ─────────────────────────────────────────────────────
        Div({ classes("hiw-trust") }) {
            listOf("🐟  Farm-fresh sourcing", "📦  Quality-checked portions", "📍  Real-time order tracking")
                .forEach { label ->
                    Div({ classes("hiw-trust-item") }) { Text(label) }
                }
        }
    }
}

@Composable
private fun HiwPathCard(
    pathLabel: String, labelClass: String, cardClass: String,
    icon: String, title: String, sub: String,
    steps: List<String>, stepNumClass: String,
    ctaText: String, ctaClass: String,
    onClick: () -> Unit
) {
    Div({
        classes("hiw-card", cardClass)
        onClick { onClick() }
    }) {
        Div({ classes("hiw-path-label", labelClass) }) { Text(pathLabel) }
        Div({ classes("hiw-card-icon") }) { Text(icon) }
        Div({ classes("hiw-card-title") }) { Text(title) }
        Div({ classes("hiw-card-sub") }) { Text(sub) }

        Div({ classes("hiw-steps") }) {
            steps.forEachIndexed { i, text ->
                Div({ classes("hiw-step") }) {
                    Div({
                        classes("hiw-step-num")
                        if (stepNumClass.isNotEmpty()) classes(stepNumClass)
                    }) { Text("${i + 1}") }
                    Span({ classes("hiw-step-text") }) { Text(text) }
                }
            }
        }

        Button({
            classes("hiw-card-cta", ctaClass)
            onClick { it.stopPropagation(); onClick() }
        }) { Text("$ctaText →") }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// ② EXPERTS — magazine-cover portrait tiles with doctor photo area
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun ExpertsSection(
    nutritionists: List<NutritionistProfile>,
    plans: List<NutritionistBasePlan>,
    loading: Boolean,
    onNavigate: (Screen) -> Unit
) {
    val fallback = listOf(
        NutritionistProfile("e1","Dr. Sarah Ahmed",  "Sports & Performance Nutrition","MSc Sports Nutrition, RD",
            "Specialised in high-performance nutrition for athletes. Expert in protein optimisation, recovery, and muscle building for competitive sports.","",8,4.8,150),
        NutritionistProfile("e2","Dr. Rajesh Kumar", "Weight Management & Metabolic Health","PhD Clinical Nutrition, RD",
            "Expert in sustainable weight loss and metabolic health. Focused on evidence-based nutrition that delivers long-term wellness results.","",12,4.9,300),
        NutritionistProfile("e3","Dr. Priya Sharma", "Family & Preventive Nutrition","MSc Food Science & Nutrition",
            "Passionate about family nutrition and disease prevention. Creates easy, practical meal plans that fit busy modern lifestyles.","",6,4.7,200)
    )
    val displayed = if (nutritionists.isNotEmpty()) nutritionists.take(3) else if (!loading) fallback else emptyList()

    // Section header with "All Experts" action
    Div({ style { marginBottom(DS.sp32) }}) {
        Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.FlexEnd); flexWrap(FlexWrap.Wrap); gap(DS.sp16) }}) {
            Div {
                Div({ classes("sec-eyebrow"); style { marginBottom(DS.sp8) }}) { Text("Expert Series") }
                Div({ classes("sec-heading") }) { Text("Trusted by Nutrition Experts") }
            }
            Button({ classes("btn-outline"); onClick { onNavigate(Screen.ProteinPlans) } }) { Text("All Experts →") }
        }
        Div({ classes("sec-subtext"); style { marginTop(DS.sp10) }}) {
            Text("Certified dietitians and clinical nutritionists curating plans on Borshi")
        }
    }

    if (loading) {
        Div({ style { display(DisplayStyle.Grid); property("grid-template-columns","repeat(3,1fr)"); gap(DS.sp20) }; classes("expert-grid") }) {
            repeat(3) {
                Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(DS.sp12) }}) {
                    Div({ classes("skeleton"); style { borderRadius(DS.r20); height(320.px) }}) {}
                    Div({ classes("skeleton"); style { height(14.px); width(55.percent) }}) {}
                    Div({ classes("skeleton"); style { height(64.px) }}) {}
                }
            }
        }
        return
    }

    Div({ style { display(DisplayStyle.Grid); property("grid-template-columns","repeat(3,1fr)"); gap(DS.sp20) }; classes("expert-grid", "fade-up") }) {
        displayed.forEachIndexed { i, expert ->
            Div({ classes("fade-up","delay-${i+1}") }) {
                ExpertPortraitTile(
                    expert      = expert,
                    expertPlans = plans.filter { it.nutritionistId == expert.nutritionistId },
                    onNavigate  = onNavigate
                )
            }
        }
    }
}

// Expert portrait tile — premium magazine-cover format
@Composable
fun ExpertPortraitTile(
    expert: NutritionistProfile,
    expertPlans: List<NutritionistBasePlan>,
    onNavigate: (Screen) -> Unit
) {
    val planCount = expertPlans.size
    val ctaLabel  = if (planCount > 0) "View $planCount plan${if (planCount != 1) "s" else ""} by ${expert.name}" else "View plans by ${expert.name}"

    Div({
        classes("expert-tile")
        makeAccessible(label = "${expert.name}, ${expert.specialization}", role = AriaRole.Button)
        onKeyActivate { onNavigate(Screen.ProteinPlans) }
        onClick { onNavigate(Screen.ProteinPlans) }
    }) {

        // ── Photo — tall 3:4 portrait ─────────────────────────────────────
        Div({ classes("expert-photo") }) {

            if (expert.profileImage.isNotBlank()) {
                Img(src = expert.profileImage, alt = expert.name, attrs = {
                    attr("loading", "lazy")
                    attr("onerror", "this.style.display='none';this.nextElementSibling.style.display='flex'")
                })
                Div({ classes("expert-initials"); style { display(DisplayStyle.None) }; ariaHidden(true) }) {
                    Div({ classes("expert-initials-letter") }) { Text(expert.name.take(1).uppercase()) }
                    Div({ classes("expert-initials-hint") }) { Text("Photo Loading") }
                }
            } else {
                Div({ classes("expert-initials"); ariaHidden(true) }) {
                    Div({ classes("expert-initials-letter") }) { Text(expert.name.take(1).uppercase()) }
                    Div({ classes("expert-initials-hint") }) { Text("Expert") }
                }
            }

            // Verified badge — top right
            Div({ classes("expert-verified"); ariaHidden(true) }) { Text("✓ Verified") }

            // Dark gradient overlay
            Div({ classes("expert-photo-overlay"); ariaHidden(true) }) {}

            // Name + quals + rating overlaid at bottom of photo
            Div({ classes("expert-photo-info") }) {
                Div({ classes("expert-name") }) { Text(expert.name) }
                if (expert.qualifications.isNotBlank()) {
                    Div({ classes("expert-quals") }) { Text(expert.qualifications) }
                }
                if (expert.rating > 0.0) {
                    Div({ classes("expert-rating-pill") }) {
                        Text("★ ${expert.rating}")
                        Span({ style { fontSize(10.px); color(Color("rgba(255,255,255,.55)")); fontWeight("400"); marginLeft(3.px) }}) {
                            Text("(${if (expert.totalClients > 0) "${expert.totalClients}+ clients" else "rated"})")
                        }
                    }
                }
            }
        }

        // ── Card body ─────────────────────────────────────────────────────
        Div({ classes("expert-body") }) {

            // Specialization pill
            if (expert.specialization.isNotBlank()) {
                Span({ classes("expert-spec") }) { Text(expert.specialization) }
            }

            // Bio — 2-line clamp
            if (expert.bio.isNotBlank()) {
                Div({ classes("expert-bio") }) { Text(expert.bio) }
            }

            // Stats: years · clients · plans
            Div({ classes("expert-stats") }) {
                listOf(
                    "${if (expert.experienceYears > 0) expert.experienceYears else "—"}+" to "Years",
                    "${if (expert.totalClients > 0) expert.totalClients else "—"}+" to "Clients",
                    "$planCount" to "Plans"
                ).forEach { (value, label) ->
                    Div({ classes("expert-stat") }) {
                        Div({ classes("expert-stat-val") }) { Text(value) }
                        Div({ classes("expert-stat-label") }) { Text(label) }
                    }
                }
            }

            // Plan pills (up to 2)
            if (expertPlans.isNotEmpty()) {
                Div({ classes("expert-plan-pills") }) {
                    expertPlans.take(2).forEach { plan ->
                        val (bg, fg) = planCategoryStyle(plan.category.name)
                        Span({ classes("expert-plan-pill"); style { backgroundColor(Color(bg)); color(Color(fg)) }}) {
                            Text("${planCategoryEmoji(plan.category.name)} ${plan.planName.take(16)}${if (plan.planName.length > 16) "…" else ""}")
                        }
                    }
                    if (expertPlans.size > 2) {
                        Span({ style { fontSize(10.px); color(Color(DS.inkLight)); padding(3.px, 4.px) }}) {
                            Text("+${expertPlans.size - 2}")
                        }
                    }
                }
            }

            Div({ style { flex(1) }}) {}

            // CTA
            Button({
                classes("expert-cta")
                ariaLabel(ctaLabel)
                tabIndex(-1)
                onClick { it.stopPropagation(); onNavigate(Screen.ProteinPlans) }
            }) {
                Text(if (planCount > 0) "View $planCount Plan${if (planCount != 1) "s" else ""} →" else "View Plans →")
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// ③ PLANS — full-size cards, pixel-identical to ProteinPlansScreen
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun PlansSection(plans: List<NutritionistBasePlan>, loading: Boolean, onNavigate: (Screen) -> Unit) {

    Div({ style { marginBottom(DS.sp32) }}) {
        Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.FlexEnd); flexWrap(FlexWrap.Wrap); gap(DS.sp16) }}) {
            Div {
                Div({ classes("sec-eyebrow"); style { marginBottom(DS.sp8) }}) { Text("Featured Plans") }
                Div({ classes("sec-heading") }) { Text("Protein Plans") }
            }
            Button({ classes("btn-outline"); onClick { onNavigate(Screen.ProteinPlans) } }) { Text("All Plans →") }
        }
        Div({ classes("sec-subtext"); style { marginTop(DS.sp10) }}) {
            Text("Expert-curated weekly seafood nutrition programmes. Tap any plan to explore.")
        }
    }

    if (loading) {
        Div({ style { display(DisplayStyle.Grid); property("grid-template-columns","repeat(3,1fr)"); gap(DS.sp20) }; classes("plan-grid") }) {
            repeat(3) {
                Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(DS.sp12) }}) {
                    Div({ classes("skeleton"); style { height(190.px); borderRadius(DS.r20) }}) {}
                    Div({ classes("skeleton"); style { height(14.px); width(65.percent) }}) {}
                    Div({ classes("skeleton"); style { height(44.px) }}) {}
                }
            }
        }
        return
    }

    if (plans.isEmpty()) {
        // Fallback category tiles
        Div({ style { display(DisplayStyle.Grid); property("grid-template-columns","repeat(auto-fill,minmax(240px,1fr))"); gap(DS.sp16) }; classes("plan-grid") }) {
            listOf(
                Triple("💪","Muscle Building",  "High protein, strength focus"),
                Triple("🔥","Weight Loss",      "Balanced, calorie optimised"),
                Triple("❤️","Heart Health",     "Omega-3 rich, heart-friendly"),
                Triple("🌿","General Wellness", "Complete nutrition balance")
            ).forEach { (emoji, title, desc) ->
                Div({
                    classes("plan-card")
                    style { padding(DS.sp28); alignItems(AlignItems.FlexStart) }
                    makeAccessible(label = "$title plan", role = AriaRole.Button)
                    onKeyActivate { onNavigate(Screen.ProteinPlans) }
                    onClick { onNavigate(Screen.ProteinPlans) }
                }) {
                    Div({ style { fontSize(36.px); marginBottom(DS.sp12) }; ariaHidden(true) }) { Text(emoji) }
                    Div({ style { fontFamily("Syne, sans-serif"); fontSize(16.px); fontWeight("800"); color(Color(DS.ink)); marginBottom(DS.sp8) }}) { Text(title) }
                    Div({ style { fontSize(13.px); color(Color(DS.inkLight)); lineHeight("1.55") }}) { Text(desc) }
                }
            }
        }
        return
    }

    Div({ style { display(DisplayStyle.Grid); property("grid-template-columns","repeat(3,1fr)"); gap(DS.sp20) }; classes("plan-grid") }) {
        plans.take(AppConfig.PLANS_DISPLAY_LIMIT).forEachIndexed { i, plan ->
            Div({ classes("fade-up","delay-${i+1}") }) {
                PlanCard(plan, onNavigate)
            }
        }
    }

    Div({ style { textAlign("center"); marginTop(DS.sp40) }}) {
        Button({ classes("btn-ink"); onClick { onNavigate(Screen.ProteinPlans) } }) { Text("See All Plans →") }
    }
}

// Plan card — mirrors ProteinPlansScreen PlanManagementCard exactly
@Suppress("unused") @Composable fun HomePlanMiniCard(plan: NutritionistBasePlan, onNavigate: (Screen) -> Unit) = PlanCard(plan, onNavigate)

@Composable
fun PlanCard(plan: NutritionistBasePlan, onNavigate: (Screen) -> Unit) {
    val emoji          = planCategoryEmoji(plan.category.name)
    val (catBg, catFg) = planCategoryStyle(plan.category.name)
    val priceLabel     = if (plan.pricingTemplate.weeklyBase > 0) "₹${plan.pricingTemplate.weeklyBase.toInt()} per week" else ""

    Div({
        classes("plan-card")
        makeAccessible(
            label = "${plan.planName}${if (priceLabel.isNotBlank()) ", $priceLabel" else ""}",
            role  = AriaRole.Button
        )
        onKeyActivate { onNavigate(Screen.ProteinPlans) }
        onClick { onNavigate(Screen.ProteinPlans) }
    }) {

        // ── Thumbnail ─────────────────────────────────────────────────────
        Div({ classes("plan-thumb") }) {
            val imgUrl = plan.imageUrl.takeIf { it.isNotBlank() && it.startsWith("http") } ?: com.example.borshi.web.firebase.FirebaseService.getPlanImageUrl(plan.category)
            Img(src = imgUrl, alt = plan.planName, attrs = {
                attr("loading", "lazy")
                attr("onerror","this.style.display='none';var n=this.nextElementSibling;if(n)n.style.display='flex'")
            })
            Span({ style { display(DisplayStyle.None); fontSize(56.px); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); width(100.percent); height(100.percent) }; ariaHidden(true) }) { Text(emoji) }
            // Category pill — top-left
            Div({ classes("plan-cat-pill"); style { backgroundColor(Color(catBg)); color(Color(catFg)) }; ariaHidden(true) }) {
                Text("$emoji ${plan.category.displayName}")
            }
            // Nutritionist credit — bottom-right
            if (plan.nutritionistName.isNotBlank()) {
                Div({ classes("plan-dr-credit"); ariaHidden(true) }) {
                    Text("👨‍⚕️ ${plan.nutritionistName.split(" ").take(2).joinToString(" ")}")
                }
            }
        }

        // ── Body ──────────────────────────────────────────────────────────
        Div({ classes("plan-body") }) {
            Div({ classes("plan-name") }) { Text(plan.planName) }

            if (plan.description.isNotBlank()) {
                Div({ classes("plan-desc") }) { Text(plan.description) }
            }

            // Nutrition chips
            Div({ classes("plan-chips") }) {
                if (plan.nutritionalTargets.proteinPerWeek > 0)
                    PlanChip("🥩 ${plan.nutritionalTargets.proteinPerWeek}g/wk", "#E8F5E9", DS.success)
                if (plan.nutritionalTargets.caloriesPerDay > 0)
                    PlanChip("🔥 ${plan.nutritionalTargets.caloriesPerDay} kcal", "#FFF3E0", DS.warn)
                if (plan.nutritionalTargets.omega3PerWeek > 0)
                    PlanChip("🐟 ${plan.nutritionalTargets.omega3PerWeek}mg", DS.tealGhost, DS.tealDeep)
            }

            Div({ classes("plan-spacer") }) {}

            // Price + CTA
            Div({ classes("plan-footer") }) {
                if (plan.pricingTemplate.weeklyBase > 0) {
                    Div {
                        Span({ classes("plan-price") }) {
                            Text("₹${plan.pricingTemplate.weeklyBase.toInt()}")
                        }
                        Span({ classes("plan-price-unit") }) { Text(" /wk") }
                    }
                } else Div {}
                Button({
                    classes("plan-view-btn")
                    ariaLabel("View ${plan.planName} details")
                    tabIndex(-1)   // parent tile is the focus target
                    onClick { it.stopPropagation(); onNavigate(Screen.ProteinPlans) }
                }) { Text("View →") }
            }
        }
    }
}

@Composable
private fun PlanChip(label: String, bg: String, fg: String) {
    Span({ classes("plan-chip"); style { backgroundColor(Color(bg)); color(Color(fg)) }}) { Text(label) }
}

// ─────────────────────────────────────────────────────────────────────────────
// Pre-order urgency banner — state driven by ViewModel ticker
// ─────────────────────────────────────────────────────────────────────────────
@Composable
fun PreOrderUrgencyBanner(timer: UrgencyTimerState = UrgencyTimerState()) {
    Div({
        classes("urgency-banner")
        style { marginBottom(DS.sp32) }
        ariaLive(AriaPoliteness.Off)          // don't spam screen readers every second
        ariaRole(AriaRole.Status)
        ariaLabel("Pre-order countdown timer: ${timer.formattedTime}")
    }) {
        Div({ style {
            width(10.px); height(10.px); borderRadius(DS.rFull); flexShrink(0)
            backgroundColor(Color(if (timer.isUrgent) DS.crimson else DS.teal))
            property("animation","subtlePulse 1.5s ease-in-out infinite")
        }}) {}
        Span({ style { fontFamily("Syne, sans-serif"); fontSize(16.px); fontWeight("800"); color(Color(DS.white)) }}) {
            Text("Pre-order ends in ${timer.formattedTime}")
        }
        Span({ style { fontSize(13.px); color(Color("rgba(255,255,255,0.68)")); fontWeight("400") }}) {
            Text("Order before 8 PM for tomorrow's fresh delivery")
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Categories
// ─────────────────────────────────────────────────────────────────────────────

/** Skeleton shimmer grid shown while category data is loading. */
@Composable
private fun CategoriesSkeleton() {
    Div({ style { display(DisplayStyle.Grid); property("grid-template-columns","repeat(auto-fit,minmax(180px,1fr))"); gap(DS.sp16) } }) {
        repeat(4) {
            Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(DS.sp8) } }) {
                Div({ classes("skeleton"); style { height(180.px); borderRadius(DS.r16) } }) {}
                Div({ classes("skeleton"); style { height(14.px); width(60.percent); borderRadius(DS.r8) } }) {}
            }
        }
    }
}

@Composable
fun CategoriesGrid(onNavigate: (Screen) -> Unit, categories: List<Category>) {
    if (categories.isEmpty()) {
        CategoriesSkeleton()
        return
    }
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(DS.sp8)
        }
    }) {
        Button({
            style {
                flexShrink(0)
                width(40.px); height(40.px)
                padding(0.px)
                borderRadius(DS.rFull)
                backgroundColor(Color(DS.border))
                color(Color(DS.ink))
                border(0.px)
                fontSize(18.px)
                fontWeight("700")
                cursor("pointer")
            }
            onClick {
                kotlinx.browser.document.getElementById("categories-scroll")?.let { el ->
                    (el.asDynamic()).scrollBy(-280.0, 0.0)
                }
            }
        }) { Text("<") }
        Div({
            id("categories-scroll")
            style {
                flex(1)
                display(DisplayStyle.Flex)
                gap(DS.sp16)
                overflowX("auto")
                overflowY("hidden")
                property("scroll-behavior", "smooth")
                property("scrollbar-width", "none")
                property("-ms-overflow-style", "none")
            }
        }) {
            categories.forEachIndexed { i, cat ->
                Div({
                    classes("fade-up", "delay-${(i % 4) + 1}")
                    style { flexShrink(0); width(180.px) }
                }) { CategoryCard(cat, onNavigate) }
            }
        }
        Button({
            style {
                flexShrink(0)
                width(40.px); height(40.px)
                padding(0.px)
                borderRadius(DS.rFull)
                backgroundColor(Color(DS.border))
                color(Color(DS.ink))
                border(0.px)
                fontSize(18.px)
                fontWeight("700")
                cursor("pointer")
            }
            onClick {
                kotlinx.browser.document.getElementById("categories-scroll")?.let { el ->
                    (el.asDynamic()).scrollBy(280.0, 0.0)
                }
            }
        }) { Text(">") }
    }
}

@Composable
fun CategoryCard(category: Category, onNavigate: (Screen) -> Unit) {
    Div({
        classes("cat-card")
        style { height(240.px); padding(DS.sp12); justifyContent(JustifyContent.SpaceBetween) }
        makeAccessible(label = "Browse ${category.name}", role = AriaRole.Button)
        onKeyActivate { onNavigate(Screen.Products(category.name)) }
        onClick { onNavigate(Screen.Products(category.name)) }
    }) {
        Div({ style { width(100.percent); flex(1); display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); alignItems(AlignItems.Center); overflow("hidden"); borderRadius(DS.r12); backgroundColor(Color(DS.tealFaint)) }}) {
            Img(src = category.imageUrl, alt = "", attrs = {   // alt="" — decorative; label on parent
                style { property("max-height","100%"); property("max-width","100%"); property("object-fit","contain") }
                attr("loading", "lazy")
                attr("onerror","this.src='${FirebaseService.getPlaceholderImageUrl()}'")
            })
        }
        Div({ style { fontFamily("Syne, sans-serif"); fontSize(16.px); fontWeight("700"); color(Color(DS.ink)); padding(DS.sp12, 0.px, DS.sp4); property("white-space","nowrap"); overflow("hidden"); property("text-overflow","ellipsis"); width(100.percent); textAlign("center") }}) {
            Text(category.name)
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Products
// ─────────────────────────────────────────────────────────────────────────────

/** Typed purchase-mode — replaces magic strings "onetime" / "subscribe". */
private enum class PurchaseMode { ONE_TIME, SUBSCRIBE }

@Composable
fun FeaturedProductsGrid(onAddToCart: (Product) -> Unit, products: List<Product>, cartStore: CartStore? = null) {
    if (products.isEmpty()) {
        Div({ style { textAlign("center"); padding(DS.sp48, 0.px); color(Color(DS.inkLight)) }}) { Text("Loading products…") }
        return
    }
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(DS.sp8)
        }
    }) {
        Button({
            style {
                flexShrink(0); width(40.px); height(40.px); padding(0.px)
                borderRadius(DS.rFull); backgroundColor(Color(DS.border))
                color(Color(DS.ink)); border(0.px)
                fontSize(18.px); fontWeight("700"); cursor("pointer")
            }
            onClick {
                kotlinx.browser.document.getElementById("featured-products-scroll")?.let { el ->
                    (el.asDynamic()).scrollBy(-300.0, 0.0)
                }
            }
        }) { Text("<") }
        Div({
            id("featured-products-scroll")
            style {
                flex(1)
                display(DisplayStyle.Flex)
                gap(DS.sp20)
                overflowX("auto")
                overflowY("hidden")
                property("scroll-behavior", "smooth")
                property("scrollbar-width", "none")
                property("-ms-overflow-style", "none")
            }
        }) {
            products.forEach { product ->
                val cartItem = cartStore?.items?.find { it.productId == product.id && it is CartLineItem.OneTime } as? CartLineItem.OneTime
                Div({ style { flexShrink(0); width(260.px) }}) {
                    DualPriceProductCard(product, onAddToCart, cartItem?.quantity ?: 0)
                }
            }
        }
        Button({
            style {
                flexShrink(0); width(40.px); height(40.px); padding(0.px)
                borderRadius(DS.rFull); backgroundColor(Color(DS.border))
                color(Color(DS.ink)); border(0.px)
                fontSize(18.px); fontWeight("700"); cursor("pointer")
            }
            onClick {
                kotlinx.browser.document.getElementById("featured-products-scroll")?.let { el ->
                    (el.asDynamic()).scrollBy(300.0, 0.0)
                }
            }
        }) { Text(">") }
    }
}

@Composable
fun DualPriceProductCard(product: Product, onAddToCart: (Product) -> Unit, cartQuantity: Int = 0) {
    var purchaseMode by remember { mutableStateOf(PurchaseMode.ONE_TIME) }
    var showInfo by remember { mutableStateOf(false) }
    val isSub        = purchaseMode == PurchaseMode.SUBSCRIBE
    val discountMult = 1.0 - AppConfig.SUBSCRIPTION_DISCOUNT_PCT
    val price        = if (isSub) (product.price * discountMult).toInt() else product.price.toInt()

    Div({
        classes("product-card")
        ariaRole(AriaRole.Region)
        ariaLabel(product.name)
    }) {
        // ── Product image ──────────────────────────────────────────────
        Div({ style { width(100.percent); height(200.px); position(Position.Relative); backgroundColor(Color(DS.tealGhost)); display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); overflow("hidden") }}) {
            Img(
                src  = if (product.imageUrl.isNotEmpty()) product.imageUrl else FirebaseService.getProductImageUrl(product.name),
                alt  = product.name,
                attrs = {
                    style { width(100.percent); height(100.percent); property("object-fit","cover") }
                    attr("loading", "lazy")
                    attr("onerror","this.src='${FirebaseService.getPlaceholderImageUrl()}'")
                }
            )
            Div({ style { position(Position.Absolute); top(DS.sp12); right(DS.sp12); backgroundColor(Color(DS.teal)); color(Color(DS.white)); padding(DS.sp4, DS.sp12); borderRadius(DS.rFull); fontSize(10.px); fontWeight("800"); property("text-transform","uppercase"); property("letter-spacing","0.06em") }
                ariaHidden(true)
            }) { Text("Fresh") }
            if (product.productInfo.isPopulated) {
                Button({
                    style {
                        position(Position.Absolute)
                        top(DS.sp12)
                        left(DS.sp12)
                        backgroundColor(Color("rgba(13,43,46,0.85)"))
                        color(Color(DS.white))
                        border(0.px)
                        width(28.px)
                        height(28.px)
                        borderRadius(DS.rFull)
                        fontSize(14.px)
                        fontWeight("700")
                        cursor("pointer")
                        property("box-shadow", DS.shadowSm)
                    }
                    ariaLabel(if (showInfo) "Hide product information for ${product.name}" else "Show product information for ${product.name}")
                    onClick {
                        it.stopPropagation()
                        showInfo = !showInfo
                    }
                }) { Text("i") }
            }
        }

        // ── Card body ──────────────────────────────────────────────────
        Div({ style { padding(DS.sp16); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(DS.sp12) }}) {
            Div({ style { fontFamily("Syne, sans-serif"); fontSize(16.px); fontWeight("700"); color(Color(DS.ink)) }}) { Text(product.name) }
            if (showInfo && product.productInfo.isPopulated) {
                Div({
                    style {
                        backgroundColor(Color(DS.tealFaint))
                        border(1.px, LineStyle.Solid, Color(DS.border))
                        borderRadius(DS.r12)
                        padding(DS.sp10, DS.sp12)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(6.px)
                        fontSize(12.px)
                        color(Color(DS.inkMid))
                    }
                }) {
                    product.productInfo.culinaryProfile.takeIf { it.isNotBlank() }?.let {
                        Div { Text(it) }
                    }
                    if (product.productInfo.healthBenefits.isNotEmpty()) {
                        Div { Text("Benefits: ${product.productInfo.healthBenefits.joinToString(", ")}") }
                    }
                    if (product.productInfo.cookingTips.isNotEmpty()) {
                        Div { Text("Tips: ${product.productInfo.cookingTips.joinToString(", ")}") }
                    }
                    if (product.productInfo.cons.isNotEmpty()) {
                        Div { Text("Notes: ${product.productInfo.cons.joinToString(", ")}") }
                    }
                    if (product.productInfo.allergens.isNotEmpty()) {
                        Div { Text("Allergens: ${product.productInfo.allergens.joinToString(", ")}") }
                    }
                    product.productInfo.storageInstructions.takeIf { it.isNotBlank() }?.let {
                        Div { Text("Storage: $it") }
                    }
                }
            }

            // ── Purchase-mode toggle ───────────────────────────────────
            Div({
                style { display(DisplayStyle.Flex); gap(DS.sp4); backgroundColor(Color(DS.surface)); padding(DS.sp4); borderRadius(DS.r12) }
                ariaRole(AriaRole.TabList)
                ariaLabel("Purchase mode for ${product.name}")
            }) {
                listOf(PurchaseMode.ONE_TIME to "One-time", PurchaseMode.SUBSCRIBE to "Subscribe · 15% off").forEach { (mode, label) ->
                    val isActive = purchaseMode == mode
                    Button({
                        classes("purchase-mode-tab")
                        style {
                            backgroundColor(if (isActive) Color(DS.white) else Color("transparent"))
                            color(if (isActive && mode == PurchaseMode.SUBSCRIBE) Color(DS.teal) else if (isActive) Color(DS.ink) else Color(DS.inkLight))
                            property("box-shadow", if (isActive) DS.shadowSm else "none")
                        }
                        ariaRole(AriaRole.Tab)
                        ariaExpanded(isActive)
                        ariaLabel(label)
                        onClick { purchaseMode = mode }
                    }) { Text(label) }
                }
            }

            // ── Price + Add-to-Cart ────────────────────────────────────
            Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); property("border-top","1px solid ${DS.border}"); paddingTop(DS.sp12) }}) {
                Div {
                    Div({ style { fontFamily("Syne, sans-serif"); fontSize(22.px); fontWeight("800"); color(Color(if (isSub) DS.teal else DS.crimson)) }}) { Text("₹$price") }
                    if (isSub) Span({ style { fontSize(12.px); color(Color(DS.inkLight)); property("text-decoration","line-through") }}) { Text("₹${product.price.toInt()}") }
                }
                Button({
                    style {
                        backgroundColor(Color(if (cartQuantity > 0) DS.success else DS.ink))
                        color(Color(DS.white)); padding(DS.sp8, DS.sp20); border(0.px)
                        borderRadius(DS.r12); fontSize(13.px); fontWeight("700"); cursor("pointer")
                        fontFamily("DM Sans, sans-serif"); property("transition","background 0.2s ease")
                    }
                    ariaLabel(if (cartQuantity > 0) "Added ${cartQuantity} of ${product.name} to cart" else "Add ${product.name} to cart")
                    onClick { onAddToCart(product) }
                }) {
                    Text(if (cartQuantity > 0) "✓ $cartQuantity added" else "Add to Cart")
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Legacy stubs
// ─────────────────────────────────────────────────────────────────────────────
@Suppress("unused") @Composable fun ProductsGrid(onAddToCart: (Product) -> Unit, products: List<Product>, cartStore: CartStore? = null) = FeaturedProductsGrid(onAddToCart, products, cartStore)
@Suppress("unused") @Composable fun ProductCard(product: Product, onAddToCart: (Product) -> Unit, qty: Int = 0) = DualPriceProductCard(product, onAddToCart, qty)
@Suppress("unused") @Composable fun HowItWorksSection() = HowBorshiPlansWork({})
@Suppress("unused") @Composable fun HomeNutritionistCard(e: NutritionistProfile, p: List<NutritionistBasePlan>) = ExpertPortraitTile(e, p, {})
@Suppress("unused") @Composable fun TrustedExpertsSection(n: List<NutritionistProfile>, p: List<NutritionistBasePlan>, l: Boolean) = ExpertsSection(n, p, l, {})